import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import * as _ from 'lodash-es';
import { finalize, merge, Subject, takeUntil } from 'rxjs';
import { AppService } from '../../../app.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import {
  buildHeaderDataShell,
  extractObject,
  gridPayloadFromActiveScreen,
  hydratedGlobalParams,
  normalizeFilterParams,
  rowPayloadFromActiveScreen
} from '../../../shared/workspace-element-context';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { ProjectTagService } from './project-tag.service';

@Component({
    selector: 'gtw-project-tag',
    templateUrl: './project-tag.component.html',
    styleUrls: ['./project-tag.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class ProjectTagComponent implements OnInit, OnDestroy {
  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  private readonly destroy$ = new Subject<void>();
  private isInitialized = false;
  private isLoading = false;
  private projectsRequestId = 0;
  private stepsRequestId = 0;

  modalTitle = 'Project Tag';
  minimise = false;
  isSaveClicked = false;
  crudLoading = false;

  entities: any[] = [];
  projectList: any[] = [];
  projectStepList: any[] = [];
  project: any = null;
  projectStep: any = null;
  projectLoading = true;
  projectStepLoading = false;
  hideProjectSteps = false;
  formSubmitted = false;

  globalService: any = {};
  GENERAL_CONFIG: any = {};
  rowData: any = {};
  gridData: any = {};
  filterParams: any = {};

  constructor(
    private projectTagService: ProjectTagService,
    private gtwSnackbarService: GtwSnackbarService,
    private cdr: ChangeDetectorRef,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService
  ) {}

  ngOnInit(): void {
    merge(this.gtwWorkspace.setTabActive, this.gtwGlobal.globalParamsChanged$)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.syncFromWorkspace());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private syncFromWorkspace(): void {
    this.applyWorkspaceContext();
    this.isInitialized = false;
    this.isLoading = false;
    this.tryInit();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const shell = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    const gc = shell.generalConfigData || {};
    this.GENERAL_CONFIG = {
      ...gc,
      base_url: gc.base_url ?? gc.api ?? ''
    };
    this.globalService = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || {};

    let fp = normalizeFilterParams(activeScreen) || {};
    if (!Object.keys(fp).length) {
      fp = extractObject(this.globalService);
    }
    this.filterParams = fp;

    const rawRow = rowPayloadFromActiveScreen(activeScreen);
    let rowObj: any = {};
    if (Array.isArray(rawRow) && rawRow.length > 0) {
      const first = rawRow[0] as any;
      rowObj = first?.data != null ? extractObject(first.data) : extractObject(first);
    } else {
      rowObj = extractObject(rawRow);
    }
    this.rowData = rowObj;

    const sel = activeScreen?.data?.selected;
    const g = gridPayloadFromActiveScreen(activeScreen);
    const rp = rowPayloadFromActiveScreen(activeScreen);
    let nextGrid: any = sel != null ? sel : g;
    if (
      nextGrid == null ||
      (typeof nextGrid === 'object' &&
        !Array.isArray(nextGrid) &&
        Object.keys(nextGrid as object).length === 0)
    ) {
      if (Array.isArray(rp) && rp.length > 0) {
        nextGrid = rp;
      } else if (rp && typeof rp === 'object' && !Array.isArray(rp) && Object.keys(rp).length > 0) {
        const raw = rp as any;
        nextGrid = raw?.data != null ? raw : { rowData: raw };
      }
    }
    const hasGridPayload =
      nextGrid != null &&
      (Array.isArray(nextGrid) ? nextGrid.length > 0 : typeof nextGrid === 'object' && Object.keys(nextGrid as object).length > 0);
    this.gridData = hasGridPayload ? nextGrid : {};
  }

  private tryInit(): void {
    if (this.isInitialized || this.isLoading) {
      return;
    }
    if (!this.GENERAL_CONFIG?.base_url || !this.gridData || Object.keys(this.gridData).length === 0) {
      return;
    }
    this.isLoading = true;
    this.isInitialized = true;
    try {
      this.init();
    } catch {
      this.isInitialized = false;
      this.isLoading = false;
    }
  }

  private init(): void {
    this.normalizeGridData();
    this.loadProjects();
  }

  private normalizeGridData(): void {
    if (!Array.isArray(this.gridData)) {
      const rowObject = this.gridData?.rowData || this.gridData || {};
      this.entities = [{ row_id: rowObject.object_id, data: rowObject }];
      if (!this.rowData || Object.keys(this.rowData).length === 0) {
        this.rowData = rowObject;
      }
      return;
    }
    this.entities = _.cloneDeep(this.gridData);
  }

  private loadProjects(): void {
    const reqId = ++this.projectsRequestId;
    this.projectLoading = true;
    this.projectTagService
      .loadProjects(this.GENERAL_CONFIG.base_url, this.globalService, this.filterParams, this.rowData)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.isLoading = false;
        })
      )
      .subscribe(
        (data: any) => {
          if (reqId !== this.projectsRequestId) {
            return;
          }
          if (data?.callSuccess !== '1') {
            this.gtwSnackbarService.openSnackBar(data?.errorMessage || 'Unable to load projects', 'gtw-snackbar--red');
          } else {
            this.projectList = data?.jsonObject || [];
            this.projectList.unshift({ PROJECT_ID: null, PROJECT_NAME: 'Un TAG Project' });
            this.setProjectFromRows();
            this.loadProjectSteps();
          }
          this.projectLoading = false;
          this.cdr.detectChanges();
        },
        () => {
          if (reqId !== this.projectsRequestId) {
            return;
          }
          this.projectLoading = false;
          this.gtwSnackbarService.openSnackBar('Error loading projects', 'gtw-snackbar--red');
          this.cdr.detectChanges();
        }
      );
  }

  private setProjectFromRows(): void {
    try {
      const projectArray: any[] = [];
      _.forEach(this.entities, (value: any) => {
        const projectId = value?.data?.PROJECT_ID;
        if (!_.includes(projectArray, projectId)) {
          projectArray.push(projectId);
        }
      });
      if (projectArray.length === 1) {
        this.project = _.find(this.projectList, { PROJECT_ID: projectArray[0] }) || null;
      }
    } catch (e) {
      console.info(e);
    }
  }

  loadProjectSteps(): void {
    if (!this.project || !this.project.PROJECT_ID) {
      this.hideProjectSteps = true;
      this.projectStep = null;
      this.projectStepList = [];
      this.cdr.detectChanges();
      return;
    }

    const reqId = ++this.stepsRequestId;
    this.hideProjectSteps = false;
    this.projectStepLoading = true;
    this.projectTagService
      .loadProjectSteps(this.GENERAL_CONFIG.base_url, this.project.PROJECT_ID, this.globalService, this.filterParams, this.rowData)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (data: any) => {
          if (reqId !== this.stepsRequestId) {
            return;
          }
          if (data?.callSuccess !== '1') {
            this.gtwSnackbarService.openSnackBar(data?.errorMessage || 'Unable to load project steps', 'gtw-snackbar--red');
            this.projectStepLoading = false;
            this.cdr.detectChanges();
            return;
          }

          this.projectStepList = data?.jsonObject || [];
          this.projectStepLoading = false;
          if (this.projectStepList.length === 0) {
            this.hideProjectSteps = true;
            this.projectStep = null;
          } else {
            this.hideProjectSteps = false;
            this.setProjectStepFromRows();
          }
          this.cdr.detectChanges();
        },
        () => {
          if (reqId !== this.stepsRequestId) {
            return;
          }
          this.projectStepLoading = false;
          this.gtwSnackbarService.openSnackBar('Error loading project steps', 'gtw-snackbar--red');
          this.cdr.detectChanges();
        }
      );
  }

  private setProjectStepFromRows(): void {
    try {
      const projectStepArray: any[] = [];
      _.forEach(this.entities, (value: any) => {
        const stepId = value?.data?.STEP_ID;
        if (!_.includes(projectStepArray, stepId)) {
          projectStepArray.push(stepId);
        }
      });
      if (projectStepArray.length === 1) {
        const projectStepId = parseInt(projectStepArray[0], 10);
        this.projectStep = _.find(this.projectStepList, { PROJECT_STEP_ID: projectStepId }) || null;
      }
    } catch (e) {
      console.info(e);
    }
  }

  removeEntity(index: number): void {
    this.entities.splice(index, 1);
    if (this.entities.length === 0) {
      this.cancel();
    }
    this.cdr.detectChanges();
  }

  private buildSavePayload(): any[] {
    const rows: any[] = [];
    _.forEach(this.entities, (entity: any) => {
      const object = entity?.data || {};
      rows.push({
        key: object.SYS_ID,
        leid: object.LEID,
        cdr_no: object.CDR_NO,
        reporting_period: object.REPORTING_PERIOD,
        bsla_code: object.BSLA_CODE,
        reporting_curr: object.CURRENCY,
        schdm_id: object.SCHDM_ID,
        schdm_source: object.SCHDM_SOURCE,
        project_id: this.project?.PROJECT_ID == null ? 0 : this.project.PROJECT_ID,
        step_id: this.projectStep?.PROJECT_STEP_ID == null ? 0 : this.projectStep.PROJECT_STEP_ID,
        posting_subtdc: object.POSTING_SUBTDC
      });
    });
    return rows;
  }

  save(): void {
    this.formSubmitted = true;
    if (!this.project) {
      this.gtwSnackbarService.openSnackBar('Please select a project', 'gtw-snackbar--yellow');
      return;
    }
    if (this.crudLoading) {
      this.gtwSnackbarService.openSnackBar('Please wait while we save this request', 'gtw-snackbar--yellow');
      return;
    }

    this.isSaveClicked = true;
    this.crudLoading = true;
    this.cdr.detectChanges();

    const payload = this.buildSavePayload();
    this.projectTagService
      .saveProjectTag(this.GENERAL_CONFIG.base_url, payload, this.globalService, this.filterParams, this.rowData)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (result: any) => {
          this.isSaveClicked = false;
          this.crudLoading = false;
          if (result?.callSuccess !== '1') {
            this.gtwSnackbarService.openSnackBar(result?.errorMessage || 'Error while saving project tag', 'gtw-snackbar--red');
          } else if (this.project?.PROJECT_ID == null) {
            this.gtwSnackbarService.openSnackBar('Project was successfully untagged', 'gtw-snackbar--green');
            this.requestGridUpdate.emit('refresh');
          } else {
            this.gtwSnackbarService.openSnackBar('Project was successfully tagged', 'gtw-snackbar--green');
            this.requestGridUpdate.emit('refresh');
          }
          this.cdr.detectChanges();
        },
        () => {
          this.isSaveClicked = false;
          this.crudLoading = false;
          this.gtwSnackbarService.openSnackBar('Error occurred while saving. Please try again.', 'gtw-snackbar--red');
          this.cdr.detectChanges();
        }
      );
  }

  cancel(): void {
    this.requestGridUpdate.emit('cancel');
  }
}
